import $ from 'jquery';
import _ from 'lodash';
// @ts-expect-error fileuploader is not typed
import qq from 'javascripts/fileuploader';
import { showAlertMessage } from 'javascripts/messages';

const dropOverlayTemplate =
  '<div class="drop-overlay"><div class="drop-overlay-text">Drop here to upload</div></div>';

class FileUploader {
  element: any;
  attachments: JQuery<HTMLElement>;
  button: JQuery<any>;
  dropzone: JQuery<HTMLElement>;
  options: PortalFileUploaderOptions;
  dropzones: HTMLElement[];
  uploader: typeof qq.FileUploader;

  constructor(element: HTMLElement, options: PortalFileUploaderOptions) {
    this.element = element;
    this.attachments = $(this.element).parents('.attachments');
    this.button = $(this.element);
    this.dropzone = this.attachments;
    this.options = options;
    _.defaults(this.options, { autoUpload: true });

    const dropzoneScope = options.dropzoneScope ?? 'auto';

    if (dropzoneScope === 'auto') {
      const dropzoneSelectors = [
        '.attachments-dropzone',
        '.note',
        '.body',
        '#new_note',
        '.full-body',
        '.control-group',
      ];

      for (const selector of dropzoneSelectors) {
        const $parent =
          selector === '.body'
            ? this.attachments.parents(selector).first()
            : $(this.element).parents(selector);

        if ($parent.length > 0) {
          this.dropzone = $parent;
          break;
        }
      }
    }

    if (this.dropzone) {
      this.dropzones = [this.dropzone[0]];
    }

    this.ensureDropOverlay();

    this.uploader = new qq.FileUploader({
      button: this.button[0],
      disableDefaultDropzone: true,
      action: this.options.url,
      extraDropzones: this.dropzones,
      autoUpload: this.options.autoUpload,

      element: this.attachments.find('.fake-attachment-button')[0],
      fileTemplate: '',
      showProgress: false,
      hideDropzones: false,
      disableCancelForFormUploads: true,
      acceptFiles: this.options.acceptFiles,

      customHeaders: {
        'X-CSRF-Token': $('meta[name=csrf-token]').attr('content'),
      },
      onEnter: (dropArea: HTMLElement) => {
        this.ensureDropOverlay();
        const $dropArea = $(dropArea);
        const dropAreaHeight = $dropArea.height();
        if (dropAreaHeight && dropAreaHeight > 150) {
          $dropArea
            .find('.drop-overlay-text')
            .css({ 'line-height': `${dropAreaHeight - 30}px` });
        }
      },

      onUpload: (_id, _fileName) => {
        this.button.find('.progress-indicator').show();
        if (this.options.onUpload) {
          this.options.onUpload();
        }
      },
      onComplete: (id, _fileName, responseJSON) => {
        this.button.find('.progress-indicator').hide();
        if (this.options.attachTo) {
          this.options.attachTo.append(responseJSON.attachmentThumb);
        } else if (this.options.onComplete) {
          this.options.onComplete(`file-upload-${id}`, responseJSON);
        } else {
          this.attachments
            .find('.attachments__body')
            .append(responseJSON.attachmentThumb);
        }
      },
      onError: (_id, _fileName, errorReason) => {
        this.button.find('.progress-indicator').hide();
        if (this.options.onError) {
          this.options.onError();
        }
        showAlertMessage(errorReason);
      },
    });
  }

  ensureDropOverlay() {
    if (this.dropzone?.find('.drop-overlay')?.length === 0) {
      this.dropzone.append(dropOverlayTemplate);
    }
  }
}

/**
 * Open the native file browser proxing requests to FileUploader
 * This is used as the callback when the editor wants to upload media.
 */
class EditorMediaUploaderCallback {
  static IMAGE_CONTENT_TYPES = [
    'image/jpeg',
    'image/pjpeg',
    'image/png',
    'image/x-png',
    'image/gif',
  ];
  static AUDIO_CONTENT_TYPES: string[] = [];
  static VIDEO_CONTENT_TYPES: string[] = [];

  constructor({
    $attachmentWindow, // in our case is the enclosing element from the editor
    uploadUrl,
    callback,
    acceptFiles = EditorMediaUploaderCallback.IMAGE_CONTENT_TYPES.join(','),
  }) {
    this.initializeFileUploader(
      $attachmentWindow,
      uploadUrl,
      acceptFiles,
      callback
    );
  }

  /**
   * Buttons in the page already have an instance of FileUploader
   * This method creates a clone of the HTML that contains some meta-info and creates a new instance
   * of the FileUploader that will be used for the editor callbacks.
   */
  private initializeFileUploader(
    $enclosingElement: JQuery<HTMLElement>,
    uploadUrl: string,
    acceptFiles: string,
    onComplete: (attachment, response) => void
  ): void {
    let existingAttachmentButtons = $enclosingElement.find(
      '.attachment-button, .attachment-link'
    );

    // Sometimes when initializing the editor the enclosing elements its just the editor field.
    // in those cases we wont be able to find buttons, but we know they should be there, so we can just move the scope one level up
    if (!existingAttachmentButtons.length) {
      $enclosingElement = $enclosingElement.parent();
      existingAttachmentButtons = $enclosingElement.find(
        '.attachment-button, .attachment-link'
      );
    }

    existingAttachmentButtons.each((_, button) => {
      const $originalButton = $(button);
      // We automatically store our file uploaders under `plugin_fileUploader`
      // We can't use that instance becase we need different options and there is no way to override them
      // Instead we create our own instance of the uploader specific for the editor
      let $fakeButton = $enclosingElement.find(
        '.fake-attachment-button--editor'
      );
      if ($fakeButton.length === 0) {
        $fakeButton = $('<div>').addClass('fake-attachment-button--editor');

        $originalButton.after($fakeButton);

        $fakeButton.fileUploader({
          url: uploadUrl,
          autoUpload: true,
          acceptFiles,
          // We re-use the progress indicator but have to manually manage it
          onComplete: (attachment, response) => {
            $enclosingElement.find('.progress-indicator').hide();
            onComplete(attachment, response);
          },
          onUpload: () => {
            $enclosingElement.find('.progress-indicator').show();
          },
          onError: () => {
            $enclosingElement.find('.progress-indicator').hide();
          },
        });
      }

      $fakeButton.find('input').trigger('click');
    });
  }
}

// @ts-expect-error avoid declaring window globals
// We alias the EditorMediaUploaderCallback to CallbackAttachmentsModal because the MediaChooser that the editor
// uses has this global hardcoded. So we give it an instance that looks like it, but instead of opening a modal
// it just opens the native file browser.
window.CallbackAttachmentsModal = EditorMediaUploaderCallback;

$.fn.fileUploader = function (options: PortalFileUploaderOptions): JQuery {
  return this.each(function () {
    if (!$.data(this, 'plugin_fileUploader')) {
      $.data(this, 'plugin_fileUploader', new FileUploader(this, options));
    }
  });
};

$(function () {
  // Enable attachment uploads.
  iripo.in('a', function (node) {
    const url = $(node).attr('href');
    if (url && url.lastIndexOf('/attachments', 0) === 0) {
      $(node).attr(
        'href',
        `/idea_portals/${$('meta[name=portal-token]').attr('content')}${url}`
      );
    }
  });

  $(document).on('click', '.attachment.gallery', function (event) {
    const $attachmentModal = $(this)
      .parents('.attachments')
      .find('.attachment-modal');
    const $ahaImageCarousel = $attachmentModal.find('aha-image-carousel');
    const newSelectedIndex = Array.from(
      event.currentTarget.parentNode.children
    ).indexOf(event.currentTarget);
    $ahaImageCarousel.attr({ 'selected-index': newSelectedIndex });
    $attachmentModal.modal();
  });
});
